import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Flame, Lock, ArrowLeft, CheckCircle } from 'lucide-react';
import Button from '../components/Button';
import Input from '../components/Input';
import { authService } from '../services/authService';

const ConfirmResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setValidating(false);
        setTokenValid(false);
        return;
      }
      const ok = await authService.validateResetToken(token);
      if (!cancelled) {
        setTokenValid(ok);
        setValidating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    setLoading(true);
    try {
      const result = await authService.confirmResetPassword(token, password);
      if (result.success) {
        setSuccess(true);
      } else {
        setError(result.message || 'Une erreur est survenue');
      }
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-primary mb-4 shadow-glow-red">
            <Flame className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">PFE</h1>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {validating ? (
            <p className="text-center text-gray-600">Vérification du lien…</p>
          ) : success ? (
            <div className="text-center py-4">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Mot de passe enregistré</h2>
              <p className="text-gray-600 mb-6">Vous pouvez vous connecter avec votre nouveau mot de passe.</p>
              <Link to="/login">
                <Button variant="primary" className="w-full">
                  Aller à la connexion
                </Button>
              </Link>
            </div>
          ) : !tokenValid ? (
            <div className="text-center py-4">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Lien invalide ou expiré</h2>
              <p className="text-gray-600 mb-6">
                Demandez un nouveau lien depuis la page « Mot de passe oublié ».
              </p>
              <Link to="/reset-password">
                <Button variant="primary" className="w-full mb-3">
                  Nouvelle demande
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="w-full" icon={ArrowLeft}>
                  Retour à la connexion
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Nouveau mot de passe</h2>
              <p className="text-gray-600 mb-6 text-sm">
                Minimum 8 caractères, un chiffre et un caractère spécial.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}
                <Input
                  label="Nouveau mot de passe"
                  type="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  icon={Lock}
                  required
                />
                <Input
                  label="Confirmer le mot de passe"
                  type="password"
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  icon={Lock}
                  required
                />
                <Button type="submit" variant="primary" className="w-full" loading={loading} disabled={loading}>
                  Enregistrer
                </Button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ConfirmResetPassword;
